import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { CoachingRecommendation } from '@coffeeplexity/shared';
import { useAppDispatch } from '../../../store';
import { updateCoachingRecommendation } from '../../../store/slices/premiumAnalyticsSlice';

interface CoachingRecommendationsViewProps {
  recommendations: CoachingRecommendation[];
}

export const CoachingRecommendationsView: React.FC<
  CoachingRecommendationsViewProps
> = ({ recommendations }) => {
  const dispatch = useAppDispatch();
  const [expandedRecommendations, setExpandedRecommendations] = useState<
    Set<string>
  >(new Set());

  const toggleExpanded = (recommendationId: string) => {
    const newExpanded = new Set(expandedRecommendations);
    if (newExpanded.has(recommendationId)) {
      newExpanded.delete(recommendationId);
    } else {
      newExpanded.add(recommendationId);
    }
    setExpandedRecommendations(newExpanded);
  };

  const handleRecommendationAction = async (
    recommendationId: string,
    action: 'accept' | 'dismiss' | 'defer'
  ) => {
    try {
      await dispatch(
        updateCoachingRecommendation({ recommendationId, action })
      ).unwrap();

      let message = '';
      switch (action) {
        case 'accept':
          message = "Recommendation accepted! We'll track your progress.";
          break;
        case 'dismiss':
          message =
            "Recommendation dismissed. We'll adjust future suggestions.";
          break;
        case 'defer':
          message = "Recommendation deferred. We'll remind you later.";
          break;
      }

      Alert.alert('Success', message);
    } catch (error) {
      Alert.alert(
        'Error',
        'Failed to update recommendation. Please try again.'
      );
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#DC2626';
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'skill_focus':
        return 'library-outline';
      case 'habit_change':
        return 'refresh-outline';
      case 'schedule_optimization':
        return 'time-outline';
      case 'content_suggestion':
        return 'document-text-outline';
      case 'motivation':
        return 'heart-outline';
      default:
        return 'bulb-outline';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return '#10B981';
      case 'moderate':
        return '#F59E0B';
      case 'challenging':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const renderRecommendationCard = (recommendation: CoachingRecommendation) => {
    const isExpanded = expandedRecommendations.has(recommendation.id);

    return (
      <View key={recommendation.id} style={styles.recommendationCard}>
        <TouchableOpacity
          style={styles.recommendationHeader}
          onPress={() => toggleExpanded(recommendation.id)}
        >
          <View style={styles.headerLeft}>
            <View
              style={[
                styles.iconContainer,
                {
                  backgroundColor: `${getPriorityColor(recommendation.priority)}20`,
                },
              ]}
            >
              <Ionicons
                name={getTypeIcon(recommendation.type) as any}
                size={20}
                color={getPriorityColor(recommendation.priority)}
              />
            </View>
            <View style={styles.headerText}>
              <Text style={styles.recommendationTitle}>
                {recommendation.title}
              </Text>
              <View style={styles.metadata}>
                <View
                  style={[
                    styles.priorityBadge,
                    {
                      backgroundColor: getPriorityColor(
                        recommendation.priority
                      ),
                    },
                  ]}
                >
                  <Text style={styles.priorityText}>
                    {recommendation.priority.toUpperCase()}
                  </Text>
                </View>
                <Text style={styles.timeframe}>{recommendation.timeframe}</Text>
                <View
                  style={[
                    styles.difficultyBadge,
                    {
                      borderColor: getDifficultyColor(
                        recommendation.difficulty
                      ),
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.difficultyText,
                      { color: getDifficultyColor(recommendation.difficulty) },
                    ]}
                  >
                    {recommendation.difficulty}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <Ionicons
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color='#9CA3AF'
          />
        </TouchableOpacity>

        <Text style={styles.description}>{recommendation.description}</Text>

        {isExpanded && (
          <View style={styles.expandedContent}>
            {/* Reasoning */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Why This Recommendation?</Text>
              <Text style={styles.reasoning}>{recommendation.reasoning}</Text>
            </View>

            {/* Expected Impact */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Expected Impact</Text>
              <View style={styles.impactGrid}>
                <View style={styles.impactItem}>
                  <Text style={styles.impactLabel}>Skill Improvement</Text>
                  <Text style={styles.impactValue}>
                    +{recommendation.expectedImpact.skillImprovement}%
                  </Text>
                </View>
                <View style={styles.impactItem}>
                  <Text style={styles.impactLabel}>Efficiency Gain</Text>
                  <Text style={styles.impactValue}>
                    +{recommendation.expectedImpact.efficiencyGain}%
                  </Text>
                </View>
                <View style={styles.impactItem}>
                  <Text style={styles.impactLabel}>Engagement</Text>
                  <Text style={styles.impactValue}>
                    +{recommendation.expectedImpact.engagementIncrease}%
                  </Text>
                </View>
                <View style={styles.impactItem}>
                  <Text style={styles.impactLabel}>Time to Results</Text>
                  <Text style={styles.impactValue}>
                    {recommendation.expectedImpact.timeToSeeResults} days
                  </Text>
                </View>
              </View>
            </View>

            {/* Action Steps */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Action Steps</Text>
              {recommendation.actionSteps.map((step, _index) => (
                <View key={step.step} style={styles.actionStep}>
                  <View style={styles.stepNumber}>
                    <Text style={styles.stepNumberText}>{step.step}</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <Text style={styles.stepDescription}>
                      {step.description}
                    </Text>
                    <View style={styles.stepMetadata}>
                      <Text style={styles.stepTime}>
                        {step.estimatedTime} min
                      </Text>
                      <Text
                        style={[
                          styles.stepDifficulty,
                          { color: getDifficultyColor(step.difficulty) },
                        ]}
                      >
                        {step.difficulty}
                      </Text>
                    </View>
                    {step.resources.length > 0 && (
                      <View style={styles.resources}>
                        <Text style={styles.resourcesTitle}>Resources:</Text>
                        {step.resources.map((resource, _resourceIndex) => (
                          <Text key={_resourceIndex} style={styles.resource}>
                            • {resource}
                          </Text>
                        ))}
                      </View>
                    )}
                  </View>
                </View>
              ))}
            </View>

            {/* Personalized Factors */}
            {recommendation.personalizedFactors.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Personalized for You</Text>
                <View style={styles.factorsList}>
                  {recommendation.personalizedFactors.map((factor, index) => (
                    <View key={index} style={styles.factorItem}>
                      <Ionicons
                        name='checkmark-circle'
                        size={16}
                        color='#10B981'
                      />
                      <Text style={styles.factorText}>{factor}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Success Metrics */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Success Metrics</Text>
              <View style={styles.metricsList}>
                {recommendation.successMetrics.map((metric, index) => (
                  <View key={index} style={styles.metricItem}>
                    <Ionicons
                      name='analytics-outline'
                      size={16}
                      color='#6750A4'
                    />
                    <Text style={styles.metricText}>{metric}</Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Confidence */}
            <View style={styles.confidenceContainer}>
              <Text style={styles.confidenceLabel}>AI Confidence</Text>
              <View style={styles.confidenceBar}>
                <View
                  style={[
                    styles.confidenceFill,
                    { width: `${recommendation.confidence * 100}%` },
                  ]}
                />
              </View>
              <Text style={styles.confidenceValue}>
                {Math.round(recommendation.confidence * 100)}%
              </Text>
            </View>
          </View>
        )}

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[styles.actionButton, styles.acceptButton]}
            onPress={() =>
              handleRecommendationAction(recommendation.id, 'accept')
            }
          >
            <Ionicons name='checkmark' size={16} color='#FFFFFF' />
            <Text style={styles.acceptButtonText}>Accept</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, styles.deferButton]}
            onPress={() =>
              handleRecommendationAction(recommendation.id, 'defer')
            }
          >
            <Ionicons name='time' size={16} color='#F59E0B' />
            <Text style={styles.deferButtonText}>Later</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, styles.dismissButton]}
            onPress={() =>
              handleRecommendationAction(recommendation.id, 'dismiss')
            }
          >
            <Ionicons name='close' size={16} color='#EF4444' />
            <Text style={styles.dismissButtonText}>Dismiss</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const sortedRecommendations = [...recommendations].sort((a, b) => {
    const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
    return (
      priorityOrder[b.priority as keyof typeof priorityOrder] -
      priorityOrder[a.priority as keyof typeof priorityOrder]
    );
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Coaching Recommendations</Text>
        <Text style={styles.sectionSubtitle}>
          Personalized suggestions to accelerate your learning journey
        </Text>

        {sortedRecommendations.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name='school-outline' size={64} color='#ccc' />
            <Text style={styles.emptyStateText}>
              No recommendations available
            </Text>
            <Text style={styles.emptyStateSubtext}>
              Keep learning to receive personalized coaching suggestions
            </Text>
          </View>
        ) : (
          <View style={styles.recommendationsList}>
            {sortedRecommendations.map(renderRecommendationCard)}
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 16,
  },
  recommendationsList: {
    gap: 16,
  },
  recommendationCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  recommendationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 6,
    lineHeight: 20,
  },
  metadata: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  priorityBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  timeframe: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  difficultyBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
  },
  difficultyText: {
    fontSize: 10,
    fontWeight: '600',
  },
  description: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 20,
    marginBottom: 16,
  },
  expandedContent: {
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 16,
    marginBottom: 16,
  },
  reasoning: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 20,
  },
  impactGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  impactItem: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
  },
  impactLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
    textAlign: 'center',
  },
  impactValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#10B981',
  },
  actionStep: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#6750A4',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    marginTop: 2,
  },
  stepNumberText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  stepContent: {
    flex: 1,
  },
  stepDescription: {
    fontSize: 14,
    color: '#1F2937',
    lineHeight: 18,
    marginBottom: 6,
  },
  stepMetadata: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8,
  },
  stepTime: {
    fontSize: 12,
    color: '#6B7280',
  },
  stepDifficulty: {
    fontSize: 12,
    fontWeight: '500',
  },
  resources: {
    backgroundColor: '#F8F9FA',
    borderRadius: 6,
    padding: 8,
  },
  resourcesTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  resource: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  factorsList: {
    gap: 6,
  },
  factorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  factorText: {
    fontSize: 14,
    color: '#4B5563',
    flex: 1,
  },
  metricsList: {
    gap: 6,
  },
  metricItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  metricText: {
    fontSize: 14,
    color: '#4B5563',
    flex: 1,
  },
  confidenceContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  confidenceLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  confidenceBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  confidenceFill: {
    height: '100%',
    backgroundColor: '#6750A4',
    borderRadius: 3,
  },
  confidenceValue: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'right',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    gap: 4,
  },
  acceptButton: {
    backgroundColor: '#10B981',
  },
  acceptButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  deferButton: {
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#F59E0B',
  },
  deferButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#F59E0B',
  },
  dismissButton: {
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#EF4444',
  },
  dismissButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#EF4444',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
});
